/* Buy-box state that follows the selected variant and the quantity on the PDP
   hero (sections/kkumi-product-hero.liquid; PDP hero v3 handoff, phase 3).

   1. Price, instantly. Dawn only refreshes the price panel after a network
      round trip, so it used to show the previous variant's price until the
      server answered (next to an Add to cart already showing the new one) and
      kept it for good if that request failed. The hero script resolves the
      variant from data-kk-variants and fires `kk:variant-local`; this redraws
      the price row from it at once: price, sale colour, struck-through
      compare-at and the "Save $X" pill, the same markup Liquid renders.
      Dawn's server copy still lands afterwards and replaces it.

   2. Products with more variants than Liquid lists (250+): a pick past the
      list can only be priced and checked by the server. While it answers,
      the price is a skeleton, Add to cart shows a spinner and "Checking
      availability…", and Buy it now is disabled (its link still points at the
      previous variant). No answer within CHECK_MS (Dawn swallows a failed
      request) and the panel shows the "from" price while Add to cart reads
      "Couldn't check. Try again" and retries instead of adding. Any answer,
      even a late one, clears it: `kk:variant-server`, or Dawn re-rendering
      <variant-selects> (also how a combination with no variant comes back).
      A combination with no variant also disables Buy it now, which would
      otherwise still hold the previous pick's checkout link.

   3. The line under Add to cart: "2 × 25-pack = 50 sleeves" (quantity 2+,
      pack-size option, product metafield kkumi.pack_unit) and the theme-wide
      "Only [n] left" (stock is only in data-kk-variants when that is on).

   Copy and formats ride data-* on the elements (section and theme settings). */
(function () {
  var CHECK_MS = 8000;

  function parse(text) { try { return JSON.parse(text || '[]'); } catch (e) { return []; } }
  function fill(tpl, map) {
    var out = String(tpl || '');
    Object.keys(map).forEach(function (k) { out = out.split('[' + k + ']').join(String(map[k])); });
    return out;
  }
  // Idempotent writes (MutationObserver feedback-loop rule, FRONTEND.md).
  function setText(node, text) { if (node && node.textContent !== text) node.textContent = text; }

  function bind(root) {
    if (root.dataset.kkBuyStateBound === '1') return;
    root.dataset.kkBuyStateBound = '1';
    var el = window.KKEl;
    var variants = parse(root.getAttribute('data-kk-variants'));
    var total = parseInt(root.getAttribute('data-kk-variants-total'), 10) || variants.length;
    var big = total > variants.length;
    var timer = null;

    function variantById(id) {
      for (var i = 0; i < variants.length; i++) if (String(variants[i].id) === String(id)) return variants[i];
      return null;
    }
    function formInput() { return root.querySelector('product-form input[name="id"]'); }
    var current = variantById(formInput() && formInput().value);

    function qty() {
      var q = root.querySelector('.quantity__input');
      var n = q ? parseInt(q.value, 10) : 1;
      return isFinite(n) && n > 0 ? n : 1;
    }

    // ---- 1. price ----------------------------------------------------------
    function cents(n) { return window.KKMoney ? window.KKMoney.fromCents(n) : ''; }
    function amountWithLabel(node, label, amount) {
      while (node.firstChild) node.removeChild(node.firstChild);
      if (label) node.appendChild(el('span', 'visually-hidden', label));
      node.appendChild(document.createTextNode(amount));
    }
    function renderPrice(v) {
      var row = root.querySelector('[data-kk-price-row]');
      if (!row || !el || !window.KKMoney || !v || typeof v.price !== 'number') return;
      // Dawn hides the row for a combination with no variant ("Unavailable").
      if (row.classList.contains('hidden')) row.classList.remove('hidden');
      var compare = typeof v.compare === 'number' ? v.compare : 0;
      var sale = compare > v.price;
      var sig = v.id + ':' + v.price + ':' + compare;
      var priceEl = row.querySelector('.pdp-panel__price');
      if (!priceEl) return;
      // Already showing exactly this (our draw, or the server's for it).
      if (row.getAttribute('data-kk-shown') === sig && priceEl.textContent.indexOf(cents(v.price)) > -1) return;
      row.setAttribute('data-kk-shown', sig);
      amountWithLabel(priceEl, sale ? row.dataset.saleLabel : '', cents(v.price));
      if (priceEl.classList.contains('pdp-panel__price--sale') !== sale) priceEl.classList.toggle('pdp-panel__price--sale', sale);
      var cmp = row.querySelector('.pdp-panel__compare');
      var save = row.querySelector('.pdp-panel__save');
      if (sale) {
        if (!cmp) { cmp = el('s', 'pdp-panel__compare'); priceEl.parentNode.insertBefore(cmp, priceEl.nextSibling); }
        amountWithLabel(cmp, row.dataset.regularLabel, cents(compare));
        if (!save) { save = el('span', 'pdp-panel__save'); cmp.parentNode.insertBefore(save, cmp.nextSibling); }
        setText(save, fill(row.dataset.saveFormat || 'Save [amount]', { amount: cents(compare - v.price) }));
      } else {
        if (cmp) cmp.remove();
        if (save) save.remove();
      }
    }

    // ---- 2. checking (250+ variants) ---------------------------------------
    function buyNow() { return root.querySelector('[data-kk-buynow]'); }
    function setState(state) {
      var checking = state === 'checking';
      var failed = state === 'failed';
      if (root.classList.contains('kk-checking') !== checking) root.classList.toggle('kk-checking', checking);
      if (root.classList.contains('kk-check-failed') !== failed) root.classList.toggle('kk-check-failed', failed);
      var atc = root.querySelector('.kk-pdp-atc-btn');
      if (atc) {
        // Only undo our own aria-busy (KKBtn sets it while adding to cart).
        if (checking) {
          atc.setAttribute('aria-busy', 'true');
          atc.dataset.kkCheckBusy = '1';
        } else if (atc.dataset.kkCheckBusy) {
          delete atc.dataset.kkCheckBusy;
          atc.removeAttribute('aria-busy');
        }
        // The retry has to be pressable; Dawn left it disabled for the request.
        if (failed) atc.disabled = false;
      }
      var bn = buyNow();
      if (bn) {
        if (checking || failed) {
          if (bn.getAttribute('aria-disabled') !== 'true') {
            bn.setAttribute('aria-disabled', 'true');
            bn.dataset.kkCheckDisabled = '1';
          }
        } else if (bn.dataset.kkCheckDisabled) {
          delete bn.dataset.kkCheckDisabled;
          // Only undo what this did: the hero sets it for a sold-out pick.
          if (bn.hasAttribute('href')) bn.removeAttribute('aria-disabled');
        }
      }
      clearTimeout(timer);
      if (checking) timer = setTimeout(function () { setState('failed'); }, CHECK_MS);
    }
    // A combination with no variant (Dawn's "Unavailable"): Buy it now would
    // still check out the previous pick. The hero's syncBuyNow puts the link
    // back for the next real variant.
    function noVariant() {
      var bn = buyNow();
      if (!bn) return;
      bn.removeAttribute('href');
      if (bn.getAttribute('aria-disabled') !== 'true') bn.setAttribute('aria-disabled', 'true');
    }
    function busyNow() { return root.classList.contains('kk-checking') || root.classList.contains('kk-check-failed'); }

    // Ask again: the same change Dawn listens for, on the current pick.
    function retry() {
      var vs = root.querySelector('variant-selects');
      var input = vs && (vs.querySelector('input[type="radio"]:checked') || vs.querySelector('select'));
      if (!input) return;
      setState('checking');
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    root.addEventListener('kk:variant-local', function (e) {
      var d = e.detail || {};
      if (d.variant) {
        current = d.variant;
        setState('');
        renderPrice(d.variant);
        syncLines();
        return;
      }
      current = null;
      syncLines();
      if (!d.complete) return;
      // Not in the list: on a 250+ variant product only the server knows;
      // otherwise every variant is listed, so this combination has none.
      if (big) setState('checking'); else noVariant();
    });
    root.addEventListener('kk:variant-server', function (e) {
      var v = e.detail && e.detail.variant;
      setState('');
      if (v) { current = variantById(v.id) || v; syncLines(); }
    });
    // Dawn re-rendered <variant-selects>: the server answered, with or
    // without a variant (no variant is Dawn's own "Unavailable").
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeName !== 'VARIANT-SELECTS') continue;
          syncAddOns();
          if (busyNow()) {
            setState('');
            var input = formInput();
            if (input && !input.value) noVariant();
          }
          return;
        }
      }
    }).observe(root, { childList: true, subtree: true });

    // Add to cart after a failed check retries instead of adding. On WINDOW in
    // the capture phase, ahead of global.js's document-level add handler.
    window.addEventListener('submit', function (e) {
      if (!root.classList.contains('kk-check-failed')) return;
      var f = e.target;
      if (!f || !root.contains(f)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      retry();
    }, true);
    // Buy it now while checking: its link still points at the old variant.
    root.addEventListener('click', function (e) {
      var bn = e.target.closest && e.target.closest('[data-kk-buynow]');
      if (!bn || !busyNow()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    }, true);

    // ---- add-on note (phase 7): "Price includes Five Pack (+$3.00)" --------
    // [value] is the picked value, [option] the option's name. For each
    // picked native option whose card carries a "+$X" line (the
    // extra over that option's cheapest value, already in the variant
    // price). Not Simple Bundles app options: the app adds those at the cart,
    // so the price shown doesn't include them.
    function optionLabel(fs) {
      var lg = fs.querySelector('legend');
      if (!lg) return '';
      var c = lg.cloneNode(true);
      Array.prototype.forEach.call(c.querySelectorAll('.var-block__dot, .val, [data-selected-value]'), function (n) { n.remove(); });
      return c.textContent.replace(/\s+/g, ' ').trim();
    }
    function syncAddOns() {
      var note = root.querySelector('[data-kk-price-note]');
      if (!note) return;
      var parts = [];
      Array.prototype.forEach.call(root.querySelectorAll('fieldset.var-block:not([data-kk-sb-entry])'), function (fs) {
        var vs = fs.closest('variant-selects');
        if (vs && vs.style.display === 'none') return; // Dawn's outgoing copy
        var r = fs.querySelector('.var-options input[type="radio"]:checked');
        if (!r || r.hasAttribute('data-kk-none')) return;
        var sub = r.closest('.var-option') && r.closest('.var-option').querySelector('.var-option__sub');
        var extra = sub ? sub.textContent.replace(/\s+/g, ' ').trim() : '';
        if (extra.charAt(0) !== '+' || !/\d/.test(extra)) return;
        var title = r.closest('.var-option').querySelector('.var-option__title');
        var value = (title ? title.textContent : r.value).replace(/\s+/g, ' ').trim();
        parts.push(fill(note.dataset.format || '[value] ([price])', { option: optionLabel(fs), value: value, price: extra }).trim());
      });
      var text = parts.join(' · ');
      setText(note, text);
      if (note.hidden !== !text) note.hidden = !text;
    }
    root.addEventListener('change', function () { setTimeout(syncAddOns, 0); });
    syncAddOns();

    // ---- 3. the line under Add to cart -------------------------------------
    function syncLines() {
      var box = root.querySelector('[data-kk-qty-help]');
      if (!box) return;
      var v = current;
      var pack = '';
      var idx = box.hasAttribute('data-pack-index') ? parseInt(box.getAttribute('data-pack-index'), 10) : -1;
      if (idx >= 0 && v && v.options) {
        var n = parseInt(String(v.options[idx]), 10);
        var q = qty();
        if (n > 1 && q > 1) {
          pack = fill(box.dataset.packFormat, { qty: q, pack: n, total: q * n, unit: box.dataset.packUnit || '' }).trim();
        }
      }
      var low = '';
      if (box.dataset.lowFormat && v && v.available && typeof v.stock === 'number') {
        var max = parseInt(box.dataset.lowMax, 10) || 10;
        if (v.stock > 0 && v.stock <= max) low = fill(box.dataset.lowFormat, { n: v.stock });
      }
      setText(box.querySelector('[data-kk-pack-line]'), pack);
      setText(box.querySelector('[data-kk-low-stock]'), low);
      var hide = !pack && !low;
      if (box.hidden !== hide) box.hidden = hide;
    }
    ['change', 'input'].forEach(function (type) {
      root.addEventListener(type, function (e) {
        if (e.target && e.target.classList && e.target.classList.contains('quantity__input')) syncLines();
      });
    });
    syncLines();
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('product-info[data-kk-variants]'), bind);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Quick view re-runs this file when it injects a hero; theme editor re-renders.
  document.addEventListener('shopify:section:load', init);
})();
